/**
 * 時間前市場の板 pivot ロジック。
 *
 * flat な BiddingMarketBid[] を 表示行 × area group (列) × 売買 に pivot する
 * 純関数 (= vitest 対象)。
 */
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  BiddingIntradayMarketAreaGroup,
  type BiddingMarketBid,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_market_model_pb'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'

/** 板が持つ 5 エリアグループ (UNSPECIFIED を除く)。 */
export const MARKET_AREA_GROUPS = [
  BiddingIntradayMarketAreaGroup.HOKKAIDO,
  BiddingIntradayMarketAreaGroup.TOHOKU_TOKYO,
  BiddingIntradayMarketAreaGroup.CHUBU_HOKURIKU_KANSAI,
  BiddingIntradayMarketAreaGroup.CHUGOKU_SHIKOKU,
  BiddingIntradayMarketAreaGroup.KYUSHU,
] as const

export type MarketAreaGroup = (typeof MARKET_AREA_GROUPS)[number]

/** エリアグループ × 量 (MW) の map 型。 */
export type AreaGroupVolumeMap = Record<MarketAreaGroup, string | undefined>

/**
 * 板の片側 (売り or 買い) の 1 価格帯。
 *
 * - price: 価格 (円/kWh)。
 * - volumes: エリアグループ別 量 (MW)。 未出品のエリアは undefined。
 * - total: この価格帯の量合計 (MW)。 量のある価格帯だけを作るので常に > 0。
 */
export type PriceLevel = {
  price: string
  volumes: AreaGroupVolumeMap
  total: string
}

/**
 * 板 pivot 後の 1 表示行。
 *
 * 売りと買いは価格で揃えず、 それぞれ最良気配から順に詰める (design
 * `intraday-price.html` の `renderBoard`)。 行 i の売りは i 番目に安い売り価格帯、
 * 買いは i 番目に高い買い価格帯で、 どちらも最良気配が最上行に来る。 片側の価格帯が
 * 尽きた行はその側が undefined になる。
 */
export type MarketBoardRow = {
  sell: PriceLevel | undefined
  buy: PriceLevel | undefined
}

function decimalToString(d: Decimal | undefined): string | undefined {
  if (d === undefined) return undefined
  return d.value !== '' ? d.value : undefined
}

function addDecimals(a: string | undefined, b: string | undefined): string {
  const numA = a !== undefined ? parseFloat(a) : 0
  const numB = b !== undefined ? parseFloat(b) : 0
  return String(numA + numB)
}

function emptyAreaMap(): AreaGroupVolumeMap {
  return {
    [BiddingIntradayMarketAreaGroup.HOKKAIDO]: undefined,
    [BiddingIntradayMarketAreaGroup.TOHOKU_TOKYO]: undefined,
    [BiddingIntradayMarketAreaGroup.CHUBU_HOKURIKU_KANSAI]: undefined,
    [BiddingIntradayMarketAreaGroup.CHUGOKU_SHIKOKU]: undefined,
    [BiddingIntradayMarketAreaGroup.KYUSHU]: undefined,
  }
}

type LevelVolumes = Map<string, Partial<Record<MarketAreaGroup, number>>>

/** 片側の価格 → エリアグループ別量 を、 量のある価格帯だけ指定の向きに並べる。 */
function toSortedLevels(
  levels: LevelVolumes,
  order: 'asc' | 'desc',
): PriceLevel[] {
  const result: PriceLevel[] = []
  for (const [price, byArea] of levels) {
    const volumes = emptyAreaMap()
    let totalNum = 0
    for (const ag of MARKET_AREA_GROUPS) {
      const v = byArea[ag]
      if (v !== undefined && v > 0) {
        volumes[ag] = String(v)
        totalNum += v
      }
    }
    if (totalNum > 0) {
      result.push({ price, volumes, total: String(totalNum) })
    }
  }
  return result.sort((a, b) =>
    order === 'asc'
      ? parseFloat(a.price) - parseFloat(b.price)
      : parseFloat(b.price) - parseFloat(a.price),
  )
}

/**
 * flat な BiddingMarketBid[] を 表示行 × area group 列 に pivot する。
 *
 * 売りは安い順 (行 0 = 最良売り = 最安値)、 買いは高い順 (行 0 = 最良買い = 最高値) に
 * 独立して並べ、 同じ行 index 同士を 1 行にまとめて返す (同じ行の売りと買いは
 * 価格が一致するとは限らない)。
 */
export function pivotMarketBoard(bids: BiddingMarketBid[]): MarketBoardRow[] {
  const sellLevels: LevelVolumes = new Map()
  const buyLevels: LevelVolumes = new Map()

  for (const bid of bids) {
    const price = decimalToString(bid.price)
    if (price === undefined) continue
    const volume = decimalToString(bid.volume)
    if (volume === undefined) continue
    const areaGroup = bid.areaGroup as MarketAreaGroup
    if (!MARKET_AREA_GROUPS.includes(areaGroup)) continue

    let levels: LevelVolumes
    if (bid.side === Side.SELL) {
      levels = sellLevels
    } else if (bid.side === Side.BUY) {
      levels = buyLevels
    } else {
      continue
    }
    let byArea = levels.get(price)
    if (byArea === undefined) {
      byArea = {}
      levels.set(price, byArea)
    }
    byArea[areaGroup] = (byArea[areaGroup] ?? 0) + parseFloat(volume)
  }

  const sells = toSortedLevels(sellLevels, 'asc')
  const buys = toSortedLevels(buyLevels, 'desc')
  const rowCount = Math.max(sells.length, buys.length)

  return Array.from(
    { length: rowCount },
    (_, i): MarketBoardRow => ({ sell: sells[i], buy: buys[i] }),
  )
}

// addDecimals は外部で使われる可能性があるため export する
export { addDecimals }

/** `pivotMarketBoard` 結果から算出したベスト売り価格・ベスト買い価格・スプレッド (AC-BIN-010)。 */
export type BestQuotes = {
  /** ベスト売り (= 売り最安値) の行 index。 存在しない板では -1。 */
  bestSellIndex: number
  /** ベスト買い (= 買い最高値) の行 index。 存在しない板では -1。 */
  bestBuyIndex: number
  bestSellPrice: string | undefined
  bestBuyPrice: string | undefined
  /** ベスト売り − ベスト買い。 いずれかが無ければ undefined。 */
  spread: string | undefined
}

/**
 * 2 つの Decimal 文字列 (`a - b`) を、 桁数に応じた固定小数点演算で計算する。
 *
 * `parseFloat` の単純な減算は 2 進浮動小数点の丸め誤差で `'1.1999999999999993'`
 * のような Decimal でない文字列を生みうる (proto Decimal を精度保持のため string で
 * 運ぶ前提が崩れる)。 価格のような小数桁が少ない値では、
 * 桁数ぶん整数へスケールしてから引くことで丸め誤差を避ける。
 */
function subtractDecimalStrings(a: string, b: string): string {
  const decimalsOf = (s: string): number => {
    const i = s.indexOf('.')
    return i === -1 ? 0 : s.length - i - 1
  }
  const scale = Math.max(decimalsOf(a), decimalsOf(b))
  const factor = 10 ** scale
  const intA = Math.round(parseFloat(a) * factor)
  const intB = Math.round(parseFloat(b) * factor)
  return ((intA - intB) / factor).toFixed(scale)
}

/**
 * 板 pivot 結果からベスト売り価格・ベスト買い価格・スプレッドを算出する (AC-BIN-010)。
 *
 * `rows` は売り・買いとも最良気配から順に詰めてある (`pivotMarketBoard` の戻り値) ので、
 * ベスト売り (= 最安値) は売りを持つ最初の行、 ベスト買い (= 最高値) は買いを
 * 持つ最初の行になる (= 板が空でなければどちらも最上行)。
 */
export function bestQuotesOf(rows: readonly MarketBoardRow[]): BestQuotes {
  const bestSellIndex = rows.findIndex((row) => row.sell !== undefined)
  const bestBuyIndex = rows.findIndex((row) => row.buy !== undefined)
  const bestSellPrice =
    bestSellIndex >= 0 ? rows[bestSellIndex].sell?.price : undefined
  const bestBuyPrice =
    bestBuyIndex >= 0 ? rows[bestBuyIndex].buy?.price : undefined
  const spread =
    bestSellPrice !== undefined && bestBuyPrice !== undefined
      ? subtractDecimalStrings(bestSellPrice, bestBuyPrice)
      : undefined
  return { bestSellIndex, bestBuyIndex, bestSellPrice, bestBuyPrice, spread }
}
